import {
  type CampaignChangeEvent,
  type CampaignType,
  CHANGE_HISTORY_PAGE_SIZES,
  type ChangeDailyCount,
  type CampaignChangeHistory as ChangeHistoryData,
  type ChangeObjectType,
  changeOperationTypeSchema,
} from "@adpilot/shared";
import { IconDownload, IconHistory } from "@tabler/icons-react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ControlButton } from "@/components/patterns/control-button";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { FilterChecklistSelect } from "@/components/patterns/filter-checklist-select";
import { FilterMultiSelect } from "@/components/patterns/filter-multi-select";
import { ReportingPeriodSelect } from "@/components/patterns/reporting-period-select";
import { SearchInput } from "@/components/patterns/SearchInput";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "@/components/ui/menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fetchCampaignChangeHistory } from "@/lib/campaign-api";
import { useFormatters } from "@/lib/format";
import { addReportingDays, type ReportingTimeZone } from "@/lib/reporting-time-zone";
import { type ReportingPeriod, resolveReportingPeriod } from "@/stores/reporting-period";
import { CampaignDetailPerformance } from "../performance/CampaignDetailPerformance";
import {
  type ChangeHistoryExportFormat,
  changeHistorySheet,
  downloadChangeHistory,
  fetchAllCampaignChanges,
} from "./change-history-export";
import { ManagedGroupHistoryPanel } from "./ManagedGroupHistoryPanel";

/** The reference's 操作对象 list, in its order. */
const OBJECT_OPTIONS = [
  "campaign",
  "ad_group",
  "product_ad",
  "keyword_target",
  "product_target",
  "audience_target",
  "negative_target",
  "automation",
  "recommendation",
  "creative",
  "ad",
] as const;
/** Objects outside Amazon's SP change history: kept visible but disabled, as in the reference. */
const UNRECORDED_OBJECTS = new Set<string>([
  "audience_target",
  "automation",
  "recommendation",
  "creative",
  "ad",
]);
/** Operation-type subjects each 操作对象 covers (see changeOperationTypeSchema). */
const SUBJECTS: Record<ChangeObjectType, readonly string[]> = {
  campaign: ["campaign"],
  ad_group: ["ad_group"],
  product_ad: ["product_ad"],
  keyword_target: ["keyword_target"],
  product_target: ["product_target", "category_target"],
  negative_target: ["negative_keyword", "negative_product", "negative_brand"],
};
const MONEY_FIELDS = new Set(["BID_AMOUNT", "BUDGET_AMOUNT"]);
const COLUMNS = [
  "object",
  "objectType",
  "objectStatus",
  "shop",
  "adGroup",
  "field",
  "previousValue",
  "newValue",
  "operator",
  "operationType",
  "occurredAt",
] as const;

export interface CampaignChangeHistoryProps {
  campaignId: string;
  shopId: string | null;
  campaignType: CampaignType;
  /** The page's reporting period — the log's own date picker starts from it. */
  period: ReportingPeriod;
  timeZone: ReportingTimeZone;
}

export function CampaignChangeHistory({
  campaignId,
  shopId,
  campaignType,
  period: initialPeriod,
  timeZone,
}: CampaignChangeHistoryProps) {
  const { t, i18n } = useTranslation("campaign");
  const text = (key: string, options?: Record<string, unknown>) =>
    t(`detail.changeHistory.${key}`, options);
  const { formatCurrency } = useFormatters();

  const [period, setPeriod] = useState<ReportingPeriod>(initialPeriod);
  const [objectTypes, setObjectTypes] = useState<ChangeObjectType[]>([]);
  const [operationTypes, setOperationTypes] = useState<string[]>([]);
  const [operators, setOperators] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  // One request per pause in typing: the term and the page reset land in the
  // same batch, so the old page is never requested with the new term.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  const [pageSize, setPageSize] = useState<number>(200);

  const dates = resolveReportingPeriod(period, new Date(), timeZone);
  const params = {
    ...dates,
    page,
    pageSize,
    objectTypes,
    operationTypes,
    operators,
    search: debouncedSearch,
    timeZone,
  };
  const isSp = campaignType === "sp";
  const query = useQuery({
    queryKey: ["campaign", campaignId, "change-history", params],
    queryFn: ({ signal }) => fetchCampaignChangeHistory(campaignId, params, signal),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled: isSp,
  });

  const data = query.data?.data;
  // dailyCounts depend on the window alone, so a filter or page change keeps
  // the last counts for this window instead of blanking the chart's markers.
  const windowKey = `${dates.startDate}|${dates.endDate}|${timeZone}`;
  const countsRef = useRef<{ key: string; counts: ChangeDailyCount[] } | null>(null);
  if (data && !query.isPlaceholderData) {
    countsRef.current = { key: windowKey, counts: data.dailyCounts };
  }
  const dailyCounts = countsRef.current?.key === windowKey ? countsRef.current.counts : null;
  const total = query.data?.pagination.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const firstPage = () => setPage(1);

  // A refetch can shrink the result below the current page.
  useEffect(() => {
    if (query.data && page > pageCount) setPage(pageCount);
  }, [query.data, page, pageCount]);
  // An operator absent from the new window can no longer be shown or matched.
  const operatorOptions = data?.operators;
  useEffect(() => {
    if (!operatorOptions) return;
    const present = new Set(operatorOptions.map((operator) => operator.key));
    setOperators((current) => {
      if (current.every((key) => present.has(key))) return current;
      setPage(1);
      return current.filter((key) => present.has(key));
    });
  }, [operatorOptions]);

  const operationOptions = changeOperationTypeSchema.options
    .filter((type) =>
      objectTypes.some((object) => SUBJECTS[object].includes(type.split(".")[0] ?? "")),
    )
    .map((type) => ({ value: type, label: text(`operationTypes.${type}`) }));

  const operatorLabel = (operator: CampaignChangeEvent["operator"]) =>
    operator.kind === "user"
      ? (operator.email ?? text("operators.deletedUser"))
      : text(`operators.${operator.kind}`);

  const fieldLabel = (field: string) =>
    i18n.exists(`campaign:detail.changeHistory.fields.${field}`) ? text(`fields.${field}`) : field;

  const valueLabel = (event: CampaignChangeEvent, value: string | null) => {
    if (value === null || value === "") return "--";
    if (MONEY_FIELDS.has(event.field) && !Number.isNaN(Number(value))) {
      return formatCurrency(Number(value), data?.currency ?? "USD");
    }
    return i18n.exists(`campaign:detail.changeHistory.values.${value}`)
      ? text(`values.${value}`)
      : value;
  };

  // Times are read in the zone the window was cut in.
  const timestamp = new Intl.DateTimeFormat("en-CA", {
    timeZone: data?.timeZone ?? timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const formatTime = (iso: string) => timestamp.format(new Date(iso)).replace(", ", " ");

  const cellTexts = (event: CampaignChangeEvent, shop: ChangeHistoryData["shop"]) => [
    event.objectName,
    text(`objectTypes.${event.objectType}`),
    event.objectStatus ? text(`status.${event.objectStatus}`) : "--",
    [shop.marketplace, shop.name].filter(Boolean).join(" "),
    event.adGroupName ?? "--",
    fieldLabel(event.field),
    valueLabel(event, event.previousValue),
    valueLabel(event, event.newValue),
    operatorLabel(event.operator),
    text(`operationTypes.${event.operationType}`),
    formatTime(event.occurredAt),
  ];
  // Leaving the tab mid-export must not start a download on another page.
  const exportAbort = useRef<AbortController | null>(null);
  useEffect(() => () => exportAbort.current?.abort(), []);
  // Everything the current filters match, not just the page on screen.
  const exportChanges = useMutation({
    mutationFn: async (format: ChangeHistoryExportFormat) => {
      const controller = new AbortController();
      exportAbort.current = controller;
      const { page: _page, pageSize: _pageSize, ...filters } = params;
      const { meta, items: all } = await fetchAllCampaignChanges(
        campaignId,
        filters,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      await downloadChangeHistory(
        format,
        `campaign-${campaignId}-change-history-${dates.startDate}_${dates.endDate}`,
        changeHistorySheet(
          COLUMNS.map((column) => text(`columns.${column}`)),
          all,
          (event) => cellTexts(event, meta.shop),
        ),
      );
    },
    onError: (error) => {
      if (error instanceof DOMException && error.name === "AbortError") return;
      toast.error(text("export.failed"));
    },
  });

  const toolbar = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <ReportingPeriodSelect
          value={period}
          timeZone={timeZone}
          onValueChange={(next) => {
            setPeriod(next);
            firstPage();
          }}
        />
        <FilterChecklistSelect
          ariaLabel={text("filters.operators")}
          placeholder={text("filters.operators")}
          moreLabel={(count) => text("filters.selectedMore", { count })}
          searchPlaceholder={text("filters.quickSearch")}
          selectAllLabel={(selected, total) => text("filters.selectAll", { selected, total })}
          selectedOnlyLabel={(count) => text("filters.selectedOnly", { count })}
          noResults={text("filters.noResults")}
          items={(data?.operators ?? []).map((operator) => ({
            value: operator.key,
            label: operatorLabel(operator),
          }))}
          value={operators}
          onValueChange={(next) => {
            setOperators(next);
            firstPage();
          }}
        />
        <FilterMultiSelect
          ariaLabel={text("filters.objects")}
          placeholder={text("filters.objects")}
          moreLabel={(count) => text("filters.selectedMore", { count })}
          items={OBJECT_OPTIONS.map((object) => ({
            value: object,
            label: text(`objectTypes.${object}`),
            disabledReason: UNRECORDED_OBJECTS.has(object)
              ? text("filters.unsupportedObject")
              : undefined,
          }))}
          value={objectTypes}
          onValueChange={(next) => {
            const objects = next as ChangeObjectType[];
            setObjectTypes(objects);
            // Operation types belong to an object; drop those no longer offered.
            setOperationTypes((current) =>
              current.filter((type) =>
                objects.some((object) => SUBJECTS[object].includes(type.split(".")[0] ?? "")),
              ),
            );
            firstPage();
          }}
        />
        <FilterMultiSelect
          ariaLabel={text("filters.operationTypes")}
          placeholder={text("filters.operationTypes")}
          moreLabel={(count) => text("filters.selectedMore", { count })}
          items={operationOptions}
          value={operationTypes}
          disabledReason={objectTypes.length === 0 ? text("filters.selectObjectFirst") : undefined}
          onValueChange={(next) => {
            setOperationTypes(next);
            firstPage();
          }}
        />
      </div>
      <div className="flex items-center gap-3">
        {data && (
          <span className="whitespace-nowrap text-[11px] text-muted-foreground">
            {data.syncedThrough
              ? text("syncedThrough", { time: formatTime(data.syncedThrough) })
              : text("notSyncedYet")}
          </span>
        )}
        <SearchInput
          aria-label={text("filters.search")}
          placeholder={text("filters.search")}
          value={search}
          showIcon
          textSize="compact"
          className="w-[200px]"
          onChange={(event) => setSearch(event.target.value)}
        />
        <Menu>
          <MenuTrigger
            render={
              <ControlButton
                variant="outline"
                controlSize="icon-toolbar"
                aria-label={text("export.label")}
                title={text("export.label")}
                disabled={total === 0 || query.isPlaceholderData || exportChanges.isPending}
              />
            }
          >
            {exportChanges.isPending ? <Spinner /> : <IconDownload />}
          </MenuTrigger>
          <MenuPopup align="end">
            <MenuItem onClick={() => exportChanges.mutate("csv")}>{text("export.csv")}</MenuItem>
            <MenuItem onClick={() => exportChanges.mutate("xlsx")}>{text("export.xlsx")}</MenuItem>
          </MenuPopup>
        </Menu>
      </div>
    </>
  );

  const items = data?.items ?? [];
  // The day of the campaign's newest change, in the zone the window is cut in.
  const latestDate = data?.latestOccurredAt
    ? new Intl.DateTimeFormat("en-CA", {
        timeZone: data.timeZone ?? timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date(data.latestOccurredAt))
    : null;
  const filtering =
    objectTypes.length > 0 || operationTypes.length > 0 || operators.length > 0 || !!search;

  const table = (
    <DataTable density="compact" stickyHeader aria-label={text("title")}>
      <TableHeader>
        <TableRow>
          {COLUMNS.map((column) => (
            <TableHead key={column} className="whitespace-nowrap">
              {text(`columns.${column}`)}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((event) => (
          <TableRow key={event.id} className="hover:bg-table-row-hover">
            <TableCell>
              <span
                className="block max-w-64 truncate text-[11px] font-semibold leading-4 text-ink"
                title={event.objectName}
              >
                {event.objectName}
              </span>
            </TableCell>
            <TableCell className="whitespace-nowrap">
              {text(`objectTypes.${event.objectType}`)}
            </TableCell>
            <TableCell className="whitespace-nowrap">
              {event.objectStatus ? text(`status.${event.objectStatus}`) : "--"}
            </TableCell>
            <TableCell className="whitespace-nowrap">
              {data?.shop.marketplace && (
                <span className="mr-1 font-mono text-[10px] text-muted-foreground">
                  {data.shop.marketplace}
                </span>
              )}
              {data?.shop.name}
            </TableCell>
            <TableCell>
              <span className="block max-w-56 truncate" title={event.adGroupName ?? undefined}>
                {event.adGroupName ?? "--"}
              </span>
            </TableCell>
            <TableCell className="whitespace-nowrap">{fieldLabel(event.field)}</TableCell>
            <TableCell className="whitespace-nowrap font-mono tabular-nums">
              {valueLabel(event, event.previousValue)}
            </TableCell>
            <TableCell className="whitespace-nowrap font-mono tabular-nums">
              {valueLabel(event, event.newValue)}
            </TableCell>
            <TableCell>
              <span className="block max-w-44 truncate" title={operatorLabel(event.operator)}>
                {operatorLabel(event.operator)}
              </span>
            </TableCell>
            <TableCell className="whitespace-nowrap">
              {text(`operationTypes.${event.operationType}`)}
            </TableCell>
            <TableCell className="whitespace-nowrap font-mono tabular-nums">
              {formatTime(event.occurredAt)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </DataTable>
  );

  if (!isSp) {
    return (
      <div className="rounded-[var(--paper-radius-composite)] border border-line bg-surface p-3.5">
        <EmptyState
          icon={<IconHistory className="size-12" />}
          title={text("empty")}
          description={text("spOnly")}
        />
      </div>
    );
  }

  const body = query.isPending ? (
    <div className="flex flex-col gap-2 p-3.5" role="status" aria-label={text("loading")}>
      {[1, 2, 3, 4].map((n) => (
        <Skeleton key={n} className="h-8 w-full" />
      ))}
    </div>
  ) : query.isError && !data ? (
    <div className="p-3.5">
      <ErrorState
        message={text("loadFailed")}
        onRetry={() => void query.refetch()}
        isRetrying={query.isFetching}
      />
    </div>
  ) : items.length === 0 ? (
    <div className="p-3.5">
      {latestDate && !filtering ? (
        <EmptyState
          icon={<IconHistory className="size-12" />}
          title={text("empty")}
          description={text("emptyWithLatest", { date: latestDate })}
          actionLabel={text("showLatest")}
          onAction={() => {
            setPeriod({
              type: "custom",
              startDate: addReportingDays(latestDate, -6),
              endDate: latestDate,
            });
            firstPage();
          }}
        />
      ) : (
        <EmptyState
          icon={<IconHistory className="size-12" />}
          title={text("empty")}
          description={text(
            filtering
              ? "noMatches"
              : data?.latestOccurredAt === null
                ? "neverChanged"
                : "emptyDescription",
          )}
        />
      )}
    </div>
  ) : (
    table
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Follows the log's own date picker; marks how many changes each bucket holds. */}
      <CampaignDetailPerformance
        campaignId={campaignId}
        shopId={shopId}
        range={period}
        timeZone={timeZone}
        // Unknown counts (loading, failed, or another window's) are not zeros.
        markers={dailyCounts ? { label: text("chartMarkers"), dailyCounts } : undefined}
        collapseStorageKey="adpilot:campaign-change-history-chart:v1"
      />
      <ManagedGroupHistoryPanel campaignId={campaignId} formatTime={formatTime} />
      <section aria-label={text("title")} aria-busy={query.isFetching}>
        <DataTableFrame
          toolbar={toolbar}
          footer={
            <DataTablePagination
              page={Math.min(page, pageCount)}
              pageCount={pageCount}
              pageSize={pageSize}
              pageSizeOptions={CHANGE_HISTORY_PAGE_SIZES}
              summary={text("count", { count: total })}
              onPageChange={setPage}
              onPageSizeChange={(next) => {
                setPageSize(next);
                firstPage();
              }}
            />
          }
        >
          {body}
        </DataTableFrame>
      </section>
    </div>
  );
}
